import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  changeDeadline, dayKey, dayStartOf, daysToDeadline, deletionBlocker, deliveryEvent, entryDuration, entryEarnings, eventSpan, formatMoney, isLive, makeId, parentTask, planDelivery,
  projectColor, projectCurrency, projectTotals, renameDelivery, resolveHourlyRate, runningEntry, setTaskDone, startTimer, stopTimer, subTasks, taskLabel, trashTask,
  type BillingMode, type Project, type Task
} from "@otrack/domain";
import { Button, Checkbox, DateField, EditableText, Field, Icon, IconButton, Menu, Segmented, Select, Switch, TextInput } from "@otrack/ui";
import { billingSummary } from "../billing-labels";
import { targetLabel } from "../commands";
import { Chip, ColorPicker, convertTyped, currencyOptions, DayBars, EmptyState, MoneyField, moneyInput, PageHeader, Panel, rise, Stat } from "../components";
import { useApp, useDataset, useFactory } from "../context";
import { dailyHours, deadlineLabel } from "../project-card";
import { href, navigate } from "../router";
import { formatClock, formatDuration, formatMonth, formatShortDate, monthRange, startOfDay } from "../time";

const BILLING_LABEL: Record<BillingMode, string> = { hourly: "Hourly", fixed: "Fixed price", "non-billable": "Non-billable" };

export function ProjectPage({ id }: { id: string }) {
  const { now, act, notify } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const project = dataset.projects[id];
  if (!project || !isLive(project)) return <div className="ot-page">
    <PageHeader title="Project not found" back={{ label: "Projects", href: href({ page: "projects" }) }}/>
    <EmptyState icon="folder" title="This project is gone">It may have been moved to Trash or never synced to this device.</EmptyState>
  </div>;

  const client = project.clientId ? dataset.clients[project.clientId] : undefined;
  const allTime = projectTotals(dataset, id, now);
  const thisMonth = projectTotals(dataset, id, now, monthRange(now));
  const currency = allTime.currency;
  const rate = allTime.effectiveHourlyRate;
  const running = runningEntry(dataset);
  const live = running?.projectId === id;
  const recent = dailyHours(dataset, now, 30, (entry) => entry.projectId === id);
  const recentTotal = recent.reduce((sum, value) => sum + value, 0);
  const due = daysToDeadline(project, now);

  const archive = () => void act([factory.op("project", id, { archivedAt: project.archivedAt ? null : now })], project.archivedAt ? `Restored ${project.name}` : `Archived ${project.name}`);
  const trash = async () => {
    const blocker = deletionBlocker(dataset, "project", id);
    if (blocker) return notify(blocker);
    await act([factory.op("project", id, { deletedAt: Date.now() })], `Moved ${project.name} to Trash`);
    navigate({ page: "projects" });
  };

  return <div className="ot-page" style={{ ["--pc" as string]: `var(--ot-pc-${projectColor(project)})` }}>
    <PageHeader
      back={{ label: "Projects", href: href({ page: "projects" }) }}
      title={<><i className="ot-swatch ot-page__swatch" aria-hidden="true"/>{project.name}</>}
      subtitle={<div className="ot-chips">
        {client ? <Chip icon="user" href={href({ page: "client", id: client.id })}>{client.name}</Chip> : <Chip icon="user" tone="muted">No client</Chip>}
        <Chip icon={project.billingMode === "non-billable" ? "info" : "coins"}>{billingSummary(dataset, project)}</Chip>
        {live && <Chip tone="live">Running</Chip>}
        {due !== null && <Chip icon="calendar-check" tone={due < 0 ? "warn" : "default"}>{deadlineLabel(due)}</Chip>}
        {project.completedAt && <Chip icon="check">Delivered</Chip>}
        {project.archivedAt && <Chip icon="archive" tone="muted">Archived</Chip>}
      </div>}
      actions={<>
        {live
          ? <Button tone="accent" icon="stop" onClick={() => void act(stopTimer(dataset, factory, Date.now()), `Stopped ${project.name}`)}>Stop timer</Button>
          : <Button tone="accent" icon="play" onClick={() => void act(startTimer(dataset, { projectId: id }, factory, Date.now()), `Started ${project.name}`)}>Start timer</Button>}
        <Menu label="Project actions" items={[
          { label: project.archivedAt ? "Unarchive" : "Archive", icon: "archive", onSelect: archive },
          { label: "Move to Trash", icon: "trash", tone: "danger", onSelect: () => void trash() }
        ]}/>
      </>}
    />
    <motion.div className="ot-kpis" {...rise(1)}>
      <Stat label="Tracked, all time" value={formatDuration(allTime.tracked)} note={`${formatDuration(allTime.billable)} billable`}/>
      <Stat label="Earned, all time" value={formatMoney({ amount: allTime.earned, currency })} note={currency}/>
      <Stat label="Effective rate" value={rate === null ? "—" : `${formatMoney({ amount: rate, currency })}/h`} note="Earned per tracked hour"/>
      <Stat label={`In ${formatMonth(now)}`} value={formatDuration(thisMonth.tracked)} note={`${formatMoney({ amount: thisMonth.earned, currency })} earned`} accent={live}/>
    </motion.div>
    <div className="ot-split">
      <div className="ot-split__main">
        <Panel title="Last 30 days" icon="calendar" aside={<span className="ot-panel__meta"><strong className="ot-mono">{formatDuration(recentTotal)}</strong> tracked</span>} index={2}>
          <DayBars days={recent} end={now} label={`Hours tracked on ${project.name} each of the last 30 days`}/>
        </Panel>
        <Tasks project={project}/>
        <Entries projectId={id}/>
      </div>
      <aside className="ot-split__side">
        <ProjectDetails project={project}/>
      </aside>
    </div>
  </div>;
}

function ProjectDetails({ project }: { project: Project }) {
  const { act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const clients = Object.values(dataset.clients).filter((client) => isLive(client) && (client.archivedAt === null || client.id === project.clientId)).sort((a, b) => a.name.localeCompare(b.name));
  const client = project.clientId ? dataset.clients[project.clientId] : undefined;
  const inherited = client?.defaultCurrency ?? dataset.settings.defaultCurrency;
  const effective = projectCurrency(dataset, project);
  const fallbackRate = resolveHourlyRate({ ...project, hourlyRate: null }, client, dataset.settings);
  const hasEntries = Object.values(dataset.timeEntries).some((entry) => isLive(entry) && entry.projectId === project.id);

  /** Saves a patch; when it changes the Project's currency, typed amounts keep their numbers. */
  const save = (patch: Partial<Project>, message: string) => {
    const next = { ...project, ...patch };
    const nextCurrency = projectCurrency(dataset, next);
    const full: Record<string, unknown> = { ...patch };
    if (nextCurrency !== effective) {
      if (!("hourlyRate" in patch)) full["hourlyRate"] = convertTyped(next.hourlyRate, effective, nextCurrency);
      if (!("fixedPrice" in patch)) full["fixedPrice"] = convertTyped(next.fixedPrice, effective, nextCurrency);
    }
    return act([factory.op("project", project.id, full)], message);
  };

  return <Panel title="Details" icon="pencil" description="Changes save as you go." className="ot-panel--sticky" index={2}>
    <div className="ot-stack">
      <Field label="Name">
        <EditableText value={project.name} maxLength={200} onCommit={async (text) => {
          const name = text.trim();
          if (!name) return "Name the project.";
          if (name !== project.name) await act([factory.op("project", project.id, { name }), ...renameDelivery(dataset, project, name, factory)], `Renamed to ${name}`);
          return null;
        }}/>
      </Field>
      <Field label="Color" hint="Shown on the Calendar, the day ribbon, and anywhere this project appears.">
        <ColorPicker value={projectColor(project)} onChange={(color) => void save({ color }, "Color changed")}/>
      </Field>
      <Field label="Client">
        <Select
          value={project.clientId ?? ""}
          searchable={clients.length > 7}
          options={[{ value: "", label: "No client", icon: "folder" }, ...clients.map((item) => ({ value: item.id, label: item.name, icon: "user" as const, hint: item.archivedAt ? "Archived" : undefined }))]}
          onChange={(clientId) => void save({ clientId: clientId || null }, clientId ? `Moved to ${dataset.clients[clientId]?.name ?? "client"}` : "Client removed")}
        />
      </Field>
      <Field label="Billing">
        <Segmented
          stretch
          value={project.billingMode}
          options={(["hourly", "fixed", "non-billable"] as const).map((mode) => ({ value: mode, label: BILLING_LABEL[mode] }))}
          onChange={(billingMode) => void save({
            billingMode,
            hourlyRate: billingMode === "hourly" ? project.hourlyRate : null,
            fixedPrice: billingMode === "fixed" ? project.fixedPrice : null
          }, `Billing set to ${BILLING_LABEL[billingMode].toLowerCase()}`)}
        />
      </Field>
      <Field label="Currency">
        <Select
          value={project.currency ?? ""}
          options={[{ value: "", label: `Same as ${client ? "client" : "account"}`, hint: inherited }, ...currencyOptions([project.currency])]}
          onChange={(code) => void save({ currency: code || null }, `Currency set to ${code || inherited}`)}
        />
      </Field>
      <AnimatePresence initial={false} mode="popLayout">
        {project.billingMode === "hourly" && <motion.div key="rate" {...reveal}>
          <Field label="Hourly rate" hint={project.hourlyRate === null ? (fallbackRate ? `Empty uses the inherited rate, ${formatMoney(fallbackRate)}/h.` : "No rate yet, so this time earns nothing.") : undefined}>
            <MoneyField amount={project.hourlyRate} currency={effective} perHour placeholder={fallbackRate ? moneyInput(fallbackRate.amount, effective) : "0"} onCommit={(hourlyRate) => save({ hourlyRate }, hourlyRate === null ? "Rate cleared" : `Rate set to ${formatMoney({ amount: hourlyRate, currency: effective })}/h`)}/>
          </Field>
        </motion.div>}
        {project.billingMode === "fixed" && <motion.div key="price" className="ot-stack" {...reveal}>
          <Field label="Project price" hint="Leave empty to price tasks one by one.">
            <MoneyField amount={project.fixedPrice} currency={effective} placeholder="Priced per task" onCommit={(fixedPrice) => save({ fixedPrice }, fixedPrice === null ? "Price cleared" : `Price set to ${formatMoney({ amount: fixedPrice, currency: effective })}`)}/>
          </Field>
          <Switch
            label="Delivered"
            description="The project price counts as earned."
            checked={project.completedAt !== null}
            onChange={(done) => void act([factory.op("project", project.id, { completedAt: done ? Date.now() : null })], done ? `${project.name} delivered` : `${project.name} reopened`)}
          />
        </motion.div>}
      </AnimatePresence>
      <Deadline project={project}/>
      {project.billingMode !== "fixed" && project.deadline !== null && <Switch
        label="Delivered"
        description="Stops the deadline countdown."
        checked={project.completedAt !== null}
        onChange={(done) => void act([factory.op("project", project.id, { completedAt: done ? Date.now() : null })], done ? `${project.name} delivered` : `${project.name} reopened`)}
      />}
      {hasEntries && <p className="ot-note"><Icon name="info"/>Rate changes apply to new time. Logged entries keep the rate they were recorded with.</p>}
    </div>
  </Panel>;
}

const deliveryDay = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" });

/** The Deadline, and the Delivery Event planned for it on the Calendar. */
function Deadline({ project }: { project: Project }) {
  const { act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const event = deliveryEvent(dataset, project);
  const span = event ? eventSpan(event) : null;

  const change = (day: string | null) => {
    if (day === project.deadline) return;
    const message = day === null ? (event ? "Deadline and delivery event cleared" : "Deadline cleared") : `Deadline set to ${formatShortDate(dayStartOf(day))}`;
    void act(changeDeadline(dataset, project, day, factory, Date.now()), message);
  };
  const plan = () => {
    if (project.deadline === null) return;
    void act(planDelivery(project, project.deadline, { allDay: true }, factory).operations, `Planned delivery on ${formatShortDate(dayStartOf(project.deadline))}`);
  };

  return <Field label="Deadline" hint={project.deadline === null ? "The day this project is due. Shown as a countdown; it never notifies." : undefined}>
    <div className="ot-stack ot-stack--tight">
      <div className="ot-deadline">
        <DateField aria-label="Deadline" value={project.deadline === null ? null : dayStartOf(project.deadline)} onChange={(day) => change(dayKey(day))}/>
        {project.deadline !== null && <IconButton type="button" label="Clear deadline" icon="close" onClick={() => change(null)}/>}
      </div>
      {project.deadline !== null && (event && span
        ? <a className="ot-deadline__event" href={href({ page: "calendar", view: "day", date: dayKey(span.start) })}>
          <Icon name="calendar-check"/><span>{event.title || "Delivery"} · {deliveryDay.format(span.start)}{event.allDay ? "" : `, ${formatClock(span.start)}–${formatClock(span.end)}`}</span>
        </a>
        : <Button type="button" tone="ghost" icon="calendar-plus" onClick={plan}>Plan a delivery event</Button>)}
    </div>
  </Field>;
}

const reveal = {
  initial: { opacity: 0, height: 0 },
  animate: { opacity: 1, height: "auto" },
  exit: { opacity: 0, height: 0, transition: { duration: 0.16 } },
  transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1] as const },
  style: { overflow: "hidden" as const }
};

type TaskView = "open" | "closed";

function Tasks({ project }: { project: Project }) {
  const { now, act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const [name, setName] = useState("");
  const [view, setView] = useState<TaskView>("open");
  // Sub-tasks sit under their parent; one whose parent is gone shows as a Task of its own.
  const tasks = Object.values(dataset.tasks).filter((task) => isLive(task) && task.projectId === project.id && !parentTask(dataset, task)).sort((a, b) => a.name.localeCompare(b.name));
  const open = tasks.filter((task) => task.doneAt === null && task.archivedAt === null);
  const closed = tasks.filter((task) => task.doneAt !== null || task.archivedAt !== null);
  const shown = view === "open" ? open : closed;
  const trackedByTask = useMemo(() => {
    const totals = new Map<string, number>();
    for (const entry of Object.values(dataset.timeEntries)) if (isLive(entry) && entry.taskId) totals.set(entry.taskId, (totals.get(entry.taskId) ?? 0) + entryDuration(entry, now));
    return totals;
  }, [dataset.timeEntries, now]);
  const tracked = (id: string) => trackedByTask.get(id) ?? 0;

  const add = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setName("");
    setView("open");
    await act([factory.op("task", makeId("task"), { projectId: project.id, name: trimmed })], `Added ${trimmed}`);
  };

  return <Panel
    title="Tasks"
    icon="target"
    index={3}
    aside={closed.length > 0 || view === "closed" ? <Segmented size="sm" aria-label="Show tasks" value={view} onChange={setView} options={[{ value: "open", label: "Open", count: open.length }, { value: "closed", label: "Done", count: closed.length }]}/> : <span className="ot-panel__meta">{open.length ? `${open.length} open` : null}</span>}
  >
    <form className="ot-task-add" onSubmit={(event) => void add(event)}>
      <TextInput
        leading={<Icon name="plus"/>}
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="Add a task and press Enter"
        aria-label="New task name"
        maxLength={200}
        trailing={<AnimatePresence>{name.trim() && <motion.kbd key="enter" className="ot-kbd" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}><Icon name="return"/></motion.kbd>}</AnimatePresence>}
      />
    </form>
    <div className="ot-tasks">
      <AnimatePresence initial={false} mode="popLayout">
        {shown.map((task) => <TaskRow key={task.id} task={task} project={project} subtasks={subTasks(dataset, task.id)} tracked={tracked}/>)}
      </AnimatePresence>
      {shown.length === 0 && <motion.p className="ot-empty ot-tasks__empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>{view === "open" ? (closed.length ? "All done. Nice." : "No tasks yet. Tasks are optional; add them to track estimates or milestone prices.") : "Nothing done or archived yet."}</motion.p>}
    </div>
  </Panel>;
}

function TaskRow({ task, project, subtasks, tracked }: { task: Task; project: Project; subtasks: Task[]; tracked: (id: string) => number }) {
  const { act, notify } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const [adding, setAdding] = useState(false);
  const currency = projectCurrency(dataset, project);
  // A Task's time includes its sub-tasks', so the estimate covers all of it.
  const total = subtasks.reduce((sum, child) => sum + tracked(child.id), tracked(task.id));
  const estimate = task.estimateMinutes === null ? null : task.estimateMinutes * 60_000;
  const progress = estimate ? Math.min(1, total / estimate) : 0;
  const over = estimate !== null && total > estimate;
  const finished = subtasks.filter((child) => child.doneAt !== null).length;
  const canAdd = task.doneAt === null && task.archivedAt === null;
  const live = runningEntry(dataset)?.taskId === task.id;
  const update = (patch: Record<string, unknown>, message: string) => act([factory.op("task", task.id, patch)], message);
  const trash = () => {
    const blocker = deletionBlocker(dataset, "task", task.id);
    if (blocker) return notify(blocker);
    void act(trashTask(dataset, factory, task.id, Date.now()), `Moved ${task.name} to Trash`);
  };

  return <motion.div
    layout="position"
    className="ot-task-group"
    initial={{ opacity: 0, y: -6 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, x: 16, transition: { duration: 0.2, delay: 0.18 } }}
    transition={{ type: "spring", stiffness: 500, damping: 40 }}
  >
    <div className={`ot-task ${task.doneAt ? "is-done" : ""} ${task.archivedAt ? "is-archived" : ""} ${live ? "is-live" : ""}`}>
      <Checkbox round checked={task.doneAt !== null} aria-label={`Mark ${task.name} done`} onChange={(done) => void act(setTaskDone(dataset, factory, task.id, done, Date.now()), done ? `Completed ${task.name}` : `Reopened ${task.name}`)}/>
      <div className="ot-task__main">
        <EditableText variant="ghost" size="sm" value={task.name} aria-label="Task name" maxLength={200} onCommit={async (text) => {
          const value = text.trim();
          if (!value) return "A task needs a name.";
          if (value !== task.name) await update({ name: value }, "Task renamed");
          return null;
        }}/>
        <div className="ot-task__meta">
          <span className={`ot-task__time ot-mono ${over ? "is-over" : ""}`}>{live && <span className="ot-chip__live" aria-hidden="true"/>}{formatDuration(total, { seconds: live })}{estimate !== null ? ` of ${formatDuration(estimate)}` : ""}</span>
          {estimate !== null && <span className={`ot-task__meter ${over ? "is-over" : ""}`}><i style={{ transform: `scaleX(${progress})` }}/></span>}
          {subtasks.length > 0 && <span className="ot-task__count"><Icon name="check-circle"/><span className="ot-mono">{finished}/{subtasks.length}</span></span>}
          {task.archivedAt && <span className="ot-tag">Archived</span>}
        </div>
      </div>
      <div className="ot-task__fields">
        <EditableText variant="ghost" size="sm" className="ot-task__estimate" value={task.estimateMinutes === null ? "" : String(Math.round(task.estimateMinutes / 6) / 10)} inputMode="decimal" placeholder="Est." aria-label="Estimate in hours" trailing={<span className="ot-unit">h</span>} onCommit={async (text) => {
          const value = text.trim();
          const minutes = value ? Math.round(Number(value.replace(",", ".")) * 60) : null;
          if (minutes !== null && !(minutes > 0)) return "Hours, like 2 or 1.5";
          if (minutes !== task.estimateMinutes) await update({ estimateMinutes: minutes }, minutes === null ? "Estimate removed" : `Estimate set to ${formatDuration(minutes * 60_000)}`);
          return null;
        }}/>
        {project.billingMode === "fixed" && <div className="ot-task__price"><MoneyField size="sm" amount={task.fixedPrice} currency={currency} placeholder="Price" aria-label={`Fixed price in ${currency}`} onCommit={(value) => update({ fixedPrice: value }, value === null ? "Price removed" : `Price set to ${formatMoney({ amount: value, currency })}`)}/></div>}
      </div>
      <TaskTimer task={task}/>
      <Menu label={`Actions for ${task.name}`} items={[
        ...(canAdd ? [{ label: "Add sub-task", icon: "subtask" as const, onSelect: () => setAdding(true) }] : []),
        { label: task.archivedAt ? "Unarchive" : "Archive", icon: "archive", onSelect: () => void update({ archivedAt: task.archivedAt ? null : Date.now() }, task.archivedAt ? "Task restored" : "Task archived") },
        { label: "Delete", icon: "trash", tone: "danger", onSelect: trash }
      ]}/>
    </div>
    {(subtasks.length > 0 || adding) && <div className="ot-subtasks">
      <AnimatePresence initial={false} mode="popLayout">
        {subtasks.map((child) => <SubTaskRow key={child.id} task={child} tracked={tracked(child.id)}/>)}
      </AnimatePresence>
      {adding && canAdd && <SubTaskAdd parent={task} onClose={() => setAdding(false)}/>}
    </div>}
  </motion.div>;
}

function SubTaskRow({ task, tracked }: { task: Task; tracked: number }) {
  const { act, notify } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const live = runningEntry(dataset)?.taskId === task.id;
  const update = (patch: Record<string, unknown>, message: string) => act([factory.op("task", task.id, patch)], message);
  const trash = () => {
    const blocker = deletionBlocker(dataset, "task", task.id);
    if (blocker) return notify(blocker);
    void act(trashTask(dataset, factory, task.id, Date.now()), `Moved ${task.name} to Trash`);
  };

  return <motion.div
    layout="position"
    className={`ot-task ot-task--sub ${task.doneAt ? "is-done" : ""} ${task.archivedAt ? "is-archived" : ""} ${live ? "is-live" : ""}`}
    initial={{ opacity: 0, y: -4 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, x: 12, transition: { duration: 0.18 } }}
    transition={{ type: "spring", stiffness: 500, damping: 40 }}
  >
    <Checkbox round checked={task.doneAt !== null} aria-label={`Mark ${task.name} done`} onChange={(done) => void act(setTaskDone(dataset, factory, task.id, done, Date.now()), done ? `Completed ${task.name}` : `Reopened ${task.name}`)}/>
    <div className="ot-task__main">
      <EditableText variant="ghost" size="sm" value={task.name} aria-label="Sub-task name" maxLength={200} onCommit={async (text) => {
        const value = text.trim();
        if (!value) return "A sub-task needs a name.";
        if (value !== task.name) await update({ name: value }, "Sub-task renamed");
        return null;
      }}/>
    </div>
    <span className="ot-task__tracked ot-mono">{live && <span className="ot-chip__live" aria-hidden="true"/>}{tracked > 0 || live ? formatDuration(tracked, { seconds: live }) : ""}{task.archivedAt && <span className="ot-tag">Archived</span>}</span>
    <TaskTimer task={task} sub/>
    <Menu label={`Actions for ${task.name}`} items={[
      { label: task.archivedAt ? "Unarchive" : "Archive", icon: "archive", onSelect: () => void update({ archivedAt: task.archivedAt ? null : Date.now() }, task.archivedAt ? "Sub-task restored" : "Sub-task archived") },
      { label: "Delete", icon: "trash", tone: "danger", onSelect: trash }
    ]}/>
  </motion.div>;
}

/** Starts a Timer on the Task, switching from whatever runs; stops it when the Task's Timer is the one running. */
function TaskTimer({ task, sub = false }: { task: Task; sub?: boolean }) {
  const { act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const running = runningEntry(dataset);
  const live = running?.taskId === task.id;
  // Done and archived Tasks take no new time; the slot stays so rows keep their columns.
  if (!live && (task.doneAt !== null || task.archivedAt !== null)) return <span className={`ot-task__timer-slot ${sub ? "is-sub" : ""}`} aria-hidden="true"/>;
  const label = targetLabel(dataset, task.projectId, task.id);
  return live
    ? <IconButton className={`ot-task__timer is-live ${sub ? "is-sub" : ""}`} label={`Stop timer on ${task.name}`} icon="stop" tooltip="Stop timer" onClick={() => void act(stopTimer(dataset, factory, Date.now()), `Stopped ${label}`)}/>
    : <IconButton className={`ot-task__timer ${sub ? "is-sub" : ""}`} label={`Start timer on ${task.name}`} icon="play" tooltip={running ? "Switch timer here" : "Start timer"} onClick={() => void act(startTimer(dataset, { projectId: task.projectId, taskId: task.id }, factory, Date.now()), `${running ? "Switched to" : "Started"} ${label}`)}/>;
}

function SubTaskAdd({ parent, onClose }: { parent: Task; onClose: () => void }) {
  const { act } = useApp();
  const factory = useFactory();
  const [name, setName] = useState("");
  const input = useRef<HTMLInputElement>(null);
  // Focus after the menu that opened this has handed focus back to its trigger.
  useEffect(() => {
    const frame = requestAnimationFrame(() => input.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  const add = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return onClose();
    setName("");
    await act([factory.op("task", makeId("task"), { projectId: parent.projectId, parentId: parent.id, name: trimmed })], `Added ${trimmed} to ${parent.name}`);
  };

  return <form className="ot-task-add ot-task-add--sub" onSubmit={(event) => void add(event)}>
    <TextInput
      ref={input}
      size="sm"
      leading={<Icon name="subtask"/>}
      value={name}
      onChange={(event) => setName(event.target.value)}
      onKeyDown={(event) => { if (event.key === "Escape") { event.stopPropagation(); onClose(); } }}
      onBlur={() => { if (!name.trim()) onClose(); }}
      placeholder="Add a sub-task and press Enter"
      aria-label={`New sub-task of ${parent.name}`}
      maxLength={200}
    />
  </form>;
}

const PAGE = 40;
const dayLabel = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });

function Entries({ projectId }: { projectId: string }) {
  const { now, openEntry } = useApp();
  const dataset = useDataset();
  const [limit, setLimit] = useState(PAGE);
  const entries = useMemo(() => Object.values(dataset.timeEntries).filter((entry) => isLive(entry) && entry.projectId === projectId).sort((a, b) => b.start - a.start), [dataset.timeEntries, projectId]);
  const shown = entries.slice(0, limit);
  const days = new Map<number, typeof shown>();
  for (const entry of shown) {
    const day = startOfDay(entry.start);
    days.set(day, [...(days.get(day) ?? []), entry]);
  }
  const today = startOfDay(now);

  return <Panel title="Time entries" icon="clock" index={4} aside={<span className="ot-panel__meta">{entries.length} total</span>}>
    {entries.length === 0 && <p className="ot-empty">No time on this project yet. Start the timer, or drag across its layer on Today.</p>}
    {[...days].map(([day, list]) => <div className="ot-day" key={day}>
      <div className="ot-day__head">
        <span>{day === today ? "Today" : day === startOfDay(now, -1) ? "Yesterday" : dayLabel.format(day)}</span>
        <span className="ot-mono">{formatDuration(list.reduce((sum, entry) => sum + entryDuration(entry, now), 0))}</span>
      </div>
      {list.map((entry) => {
        const task = entry.taskId ? dataset.tasks[entry.taskId] : undefined;
        const value = entryEarnings(entry, now);
        const kind = entry.end === null ? "running" : entry.billable ? "billable" : "unbilled";
        return <button type="button" className="ot-entry" key={entry.id} onClick={() => openEntry(entry.id)}>
          <span className={`ot-entry__swatch is-${kind}`} aria-hidden="true"/>
          <span className="ot-entry__time ot-mono">{formatClock(entry.start)}–{entry.end === null ? "now" : formatClock(entry.end)}</span>
          <span className="ot-entry__desc">{entry.description || <em>No description</em>}{task ? <span className="ot-entry__task">{taskLabel(dataset, task)}</span> : null}</span>
          <span className="ot-entry__value ot-mono">{value ? formatMoney(value) : entry.billable ? "" : "Non-billable"}</span>
          <span className="ot-entry__dur ot-mono">{formatDuration(entryDuration(entry, now))}</span>
        </button>;
      })}
    </div>)}
    {entries.length > limit && <Button tone="ghost" className="ot-more" onClick={() => setLimit(limit + PAGE)}>Show {Math.min(PAGE, entries.length - limit)} more</Button>}
  </Panel>;
}
